import styles from '../_signup.module.scss'
import giftStyles from '../../gift_subscription/_giftSubscriptionPage.module.scss'
import { t } from '../../../utils/i18n'
import classnames from 'classnames'
import classNames from 'classnames'
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement
} from '@stripe/react-stripe-js'
import { useEffect, useState } from 'react'
import Image from 'next/legacy/image'
import artistImg from '@public/images/artist.png'
import { useRouter } from 'next/router'
import { useTheme } from 'next-themes'
import { useLazyQuery } from '@apollo/client'
import { COUPON_VALIDATION_QUERY } from '../../../gql'

interface PaymentProps {
  register: any
  couponCode?: string
  planId?: number
  errors: any
  isSubmitted: boolean
}

const StripeFields = () => {
  const { theme } = useTheme()

  const STRIPE_INPUT_DARK = {
    borderColor: '#333333 #333333 #828282 #333333 !important',
    color: '#fff',
    '::placeholder': {
      color: '#525252'
    }
  }
  const STRIPE_INPUT_LIGHT = {
    borderColor: '#fafafa #fafafa #828282 #fafafa !important',
    color: '#676767',
    '::placeholder': {
      color: '#cecece'
    }
  }

  const STRIPE_INPUT_BASE =
    theme == 'dark' ? STRIPE_INPUT_DARK : STRIPE_INPUT_LIGHT

  const STRIPE_INPUT_OPTIONS = {
    style: {
      base: {
        border: '1px solid !important',
        fontWeight: '500',
        fontFamily:
          '"AvenirNext", "Fira Sans", "Droid Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
        fontSize: '24px',
        fontSmoothing: 'antialiased',
        ':-webkit-autofill': {
          color: '#fce883'
        },
        '::placeholder': {
          fontSize: '24px',
          fontWeight: '500',
          lineHeight: '35px',
          fontFamily:
            '"AvenirNext", "Fira Sans", "Droid Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
          ...STRIPE_INPUT_BASE['::placeholder']
        },
        ...STRIPE_INPUT_BASE
      },
      invalid: {
        iconColor: '#FFC7EE',
        color: '#FFC7EE'
      }
    }
  }

  const [stripeErrors, setStripeErrors] = useState({
    cardNumber: null,
    cardExpression: null,
    CVC: null
  })

  return (
    <>
      <label>
        <span>{t('Credit Card Number')}</span>
        <div className={styles.paymentInput}>
          <CardNumberElement
            options={{
              ...STRIPE_INPUT_OPTIONS,
              placeholder: '1234 1234 1234 1234'
            }}
            onChange={({ error }) => {
              if (error) {
                setStripeErrors((errors) => ({
                  ...errors,
                  cardNumber: error.message
                }))
              } else {
                setStripeErrors((errors) => ({ ...errors, cardNumber: null }))
              }
            }}
          />
        </div>
        <p className={styles.testFormError}>{stripeErrors.cardNumber}</p>
      </label>
      <label>
        <span>{t('Expiration')}</span>
        <div className={styles.paymentInput}>
          <CardExpiryElement
            options={STRIPE_INPUT_OPTIONS}
            onChange={({ error }) => {
              if (error) {
                setStripeErrors((errors) => ({
                  ...errors,
                  cardExpression: error.message
                }))
              } else {
                setStripeErrors((errors) => ({
                  ...errors,
                  cardExpression: null
                }))
              }
            }}
          />
        </div>
        <p className={styles.testFormError}>{stripeErrors.cardExpression}</p>
      </label>
      <label>
        <span>{t('CVC')}</span>
        <div className={styles.paymentInput}>
          <CardCvcElement
            options={STRIPE_INPUT_OPTIONS}
            onChange={({ error }) => {
              if (error) {
                setStripeErrors((errors) => ({ ...errors, CVC: error.message }))
              } else {
                setStripeErrors((errors) => ({ ...errors, CVC: null }))
              }
            }}
          />
        </div>
        <p className={styles.testFormError}>{stripeErrors.CVC}</p>
      </label>
    </>
  )
}

const Payment: React.FC<PaymentProps> = ({
  register,
  couponCode,
  planId,
  errors,
  isSubmitted
}) => {
  const [stripeFields, setStripeFields] = useState(null)
  const [couponName, setCouponName] = useState(null)
  const [isCouponValid, setIsCouponValid] = useState(true)

  const router = useRouter()
  const isGiftPage = router.route == '/gift_subscription'

  useEffect(() => {
    setStripeFields(<StripeFields />)
  }, [])

  const [validateCoupon, { loading }] = useLazyQuery(COUPON_VALIDATION_QUERY, {
    fetchPolicy: 'no-cache'
  })

  const applyCoupon = () => {
    validateCoupon({
      variables: { code: couponCode, planId: planId },
      onCompleted: (data) => {
        setIsCouponValid(!!data.couponValidation.couponCode)
        setCouponName(data.couponValidation.name)
      }
    })
  }

  return (
    <div>
      <div
        className={classNames(
          styles.signupForm,
          isGiftPage && giftStyles.signupForm
        )}
      >
        <div className={styles.paymentForm}>
          {stripeFields}

          <label
            className={classnames(styles.payment, {
              [styles.errorField]: errors['zipCode']
            })}
          >
            <span>{t('Zip Code')}</span>
            <input
              type="text"
              placeholder="90210"
              name="zipCode"
              ref={register({ required: t('Is required') })}
            />
            <p className={styles.testFormError}>{errors.zipCode?.message}</p>
          </label>
          {!isGiftPage && (
            <>
              <label className={styles.payment}>
                <div className={styles.couponHeader}>
                  <span>{t('Coupon Code')}</span>
                  {couponCode && (
                    <button
                      className={classNames(styles.couponButton)}
                      type="button"
                      disabled={loading}
                      onClick={applyCoupon}
                    >
                      {t('Apply')}
                    </button>
                  )}
                </div>

                <input
                  type="text"
                  name="couponCode"
                  ref={register()}
                  onChange={() => setCouponName(null)}
                />
                <p className={styles.testFormError}>
                  {errors.couponCode?.message}
                </p>
              </label>
              {couponName && (
                <p
                  className={classNames(styles.couponDescription, {
                    [styles.couponDescriptionInvalid]: !isCouponValid
                  })}
                >
                  {isCouponValid ? `${couponName} - Applied` : couponName}
                </p>
              )}
            </>
          )}
        </div>
        <button className={styles.button} type="submit" disabled={isSubmitted}>
          <span>{isGiftPage ? t('Submit') : t('Activate My Account')}</span>
        </button>
      </div>
      <div className={styles.signupPicture}>
        <Image alt="Artist" src={artistImg} height={535} width={696} />
        {isGiftPage && (
          <img className={styles.giftPicture} src="../images/gift.svg" />
        )}
      </div>
    </div>
  )
}

export default Payment
